import { useEffect, useState } from "react";
import { staffApi, ApiError } from "@/api";
import type { QuoteEngagement as Engagement } from "@/types";
import { Button, Spinner } from "@/components/ui";
import {
  SectionDwell,
  SectionOverlapFootnote,
  SessionRow,
  ago,
  stamped,
} from "@/staff/engagement/EngagementPanel";

/**
 * How the client read the quote (api 335) — the quote editor's counterpart of the lead
 * drawer's ReportEngagement, composed from the same shared primitives.
 *
 * It leads on **did they open it**, because that is the thing staff act on: opened-and-silent
 * is a nudge, never-opened is a resend. Staff opens are excluded server-side (the browser's
 * staff JWT flags the session), so what the panel shows is the client.
 *
 * One caveat the report panel doesn't need: quote view counts PREDATE per-session tracking
 * (they come from api 181, live since the quote pages shipped), so view_count > 0 with zero
 * sessions means "opened before tracking existed, or with JS off" — the panel says that
 * rather than showing depth-of-read machinery with nothing in it.
 */

/** Block-type → staff-facing label. Falls back to a de-underscored key for future types. */
const SECTION_LABELS: Record<string, string> = {
  cover: "Cover",
  cd_intro: "Introduction",
  rich_text: "Copy",
  image: "Imagery",
  split: "Side-by-side",
  feature_grid: "Feature grid",
  quote: "Testimonial",
  author: "Author note",
  embed: "Embedded media",
  cd_drawings: "Drawings",
  cd_vr: "VR walkthrough",
  pricing: "Pricing",
  cd_terms: "Terms",
  terms: "Terms",
  accept: "Accept & sign",
};

function labelFor(key: string): string {
  return SECTION_LABELS[key] ?? key.replace(/_/g, " ");
}

export function QuoteEngagement({ quoteId }: { quoteId: number }) {
  const [data, setData] = useState<Engagement | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [showSessions, setShowSessions] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    staffApi
      .getQuoteEngagement(quoteId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Couldn't load engagement.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId]);

  if (loading) return <div className="reng reng--loading"><Spinner /></div>;
  // A failure here must not imply "not opened" — say the panel is unavailable and stop.
  if (error) return <p className="reng__note" role="status">{error}</p>;
  if (!data?.quote) return null;

  const q = data.quote;
  const opened = q.view_count > 0;
  const excluded = Math.max(0, (data.total_sessions ?? 0) - data.sessions.length);
  // Views recorded before per-session tracking shipped (Aug 2026): real opens, no detail.
  const preTracking = opened && (data.total_sessions ?? 0) === 0;

  return (
    <div className="reng">
      <div className={`reng__head${opened ? " reng__head--opened" : ""}`}>
        {opened ? (
          <>
            <strong>Opened {q.view_count === 1 ? "once" : `${q.view_count} times`}</strong>
            <span className="reng__meta">
              first {ago(q.first_viewed_at)} · last {ago(q.last_viewed_at)}
            </span>
          </>
        ) : (
          <>
            {/* "by the client" is the precise claim — staff opens are flagged and don't count. */}
            <strong>Not opened by the client</strong>
            {/* Published-and-ignored versus never-published are the same zero and call for
                opposite actions: nudge them, or publish it. Say which one this is. */}
            <span className="reng__meta">
              {stamped(q.sent_at)
                ? `published ${ago(q.sent_at)} — worth a nudge`
                : "and it has not been published yet"}
            </span>
          </>
        )}
      </div>

      {preTracking ? (
        <p className="reng__note">
          These opens predate per-visit tracking (or ran without JavaScript), so there is no
          depth detail for them — dwell and scroll start recording from the next open.
        </p>
      ) : null}

      {excluded > 0 ? (
        <p className="reng__note reng__note--excluded">
          {excluded} staff open{excluded === 1 ? "" : "s"} excluded — your own visits to the
          public page are flagged by your staff login and never count as the client reading it.
        </p>
      ) : null}

      {data.section_totals.length > 0 ? (
        <>
          <span className="reng__legend">Time per section</span>
          <SectionDwell totals={data.section_totals} labelFor={labelFor} />
          <SectionOverlapFootnote />
        </>
      ) : opened && !preTracking ? (
        <p className="reng__note">
          No per-section detail yet — dwell is recorded once a reader scrolls through with
          JavaScript running.
        </p>
      ) : null}

      {data.sessions.length > 0 ? (
        <div className="reng__more">
          <Button variant="ghost" size="sm" onClick={() => setShowSessions((v) => !v)}>
            {showSessions ? "Hide" : "Show"} {data.sessions.length} session
            {data.sessions.length === 1 ? "" : "s"}
          </Button>
          {showSessions ? (
            <div className="reng__sessions">
              {data.sessions.map((s) => (
                <SessionRow key={s.id} s={s} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
